import { Kind, OperationTypeNode, type ValueNode } from 'graphql';
import { makeOperation, mapExchange, type Operation } from 'urql';

import { Storage, StorageKey, StorageKind } from '@revelio/core';

let isSuppressed = false;

export const isTopNOverrideSuppressed = () => isSuppressed;

export const setTopNOverrideSuppressed = (suppressed: boolean) => {
  isSuppressed = suppressed;
};

export const getTopNOverride = (): number | null => {
  if (isSuppressed) return null;
  const value = Storage.readJson<unknown>(
    StorageKind.Session,
    StorageKey.CompositionTopNOverride,
    null
  );
  return isValidTopN(value) ? value : null;
};

export const setTopNOverride = (value: number | null) => {
  if (value === null) {
    try {
      Storage.getStorageProvider(StorageKind.Session)?.removeItem(
        StorageKey.CompositionTopNOverride
      );
    } catch {
      console.warn('Failed to remove item from storage');
    }
    return;
  }
  Storage.writeJson(
    StorageKind.Session,
    StorageKey.CompositionTopNOverride,
    value
  );
};

export const reloadPage = () => window.location.reload();

export const MAX_TOP_N = 2000;

export const isValidTopN = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value > 0 &&
  value <= MAX_TOP_N;

const topNOverrideAtLoad = getTopNOverride();
let isReloadPending = false;

/**
 * Stores the override and reports whether it stuck. The urql cache still holds
 * results fetched with the value the page loaded with, so a changed override
 * leaves a reload pending for the next navigation.
 */
export const saveTopNOverride = (value: number | null): boolean => {
  setTopNOverride(value);
  if (getTopNOverride() !== value) return false;
  isReloadPending = value !== topNOverrideAtLoad;
  return true;
};

export const isTopNOverrideReloadPending = () => isReloadPending;

type Variables = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const overrideBreakdownTopN = (
  breakdownOptions: unknown,
  topN: number
): Record<string, unknown> | null => {
  if (!isRecord(breakdownOptions)) return null;

  const topNKeys = Object.keys(breakdownOptions).filter(
    (key) => key.endsWith('_top_n') && typeof breakdownOptions[key] === 'number'
  );
  if (topNKeys.length === 0) return null;

  return {
    ...breakdownOptions,
    ...Object.fromEntries(topNKeys.map((key) => [key, topN])),
  };
};

const overrideReqArgument = (
  reqValue: ValueNode,
  variables: Variables,
  topN: number
): Variables | null => {
  if (reqValue.kind === Kind.VARIABLE) {
    const varName = reqValue.name.value;
    const req = variables[varName];
    if (!isRecord(req)) return null;

    const breakdownOptions = overrideBreakdownTopN(req.breakdown_options, topN);
    if (!breakdownOptions) return null;

    return {
      ...variables,
      [varName]: { ...req, breakdown_options: breakdownOptions },
    };
  }

  if (reqValue.kind === Kind.OBJECT) {
    const field = reqValue.fields.find(
      (f) => f.name.value === 'breakdown_options'
    );
    if (field?.value.kind !== Kind.VARIABLE) return null;

    const varName = field.value.name.value;
    const breakdownOptions = overrideBreakdownTopN(variables[varName], topN);
    if (!breakdownOptions) return null;

    return { ...variables, [varName]: breakdownOptions };
  }

  return null;
};

const applyTopNOverride = (operation: Operation, topN: number): Operation => {
  let variables: Variables = { ...operation.variables };
  let changed = false;

  for (const definition of operation.query.definitions) {
    if (
      definition.kind !== Kind.OPERATION_DEFINITION ||
      definition.operation !== OperationTypeNode.QUERY
    ) {
      continue;
    }

    for (const selection of definition.selectionSet.selections) {
      if (
        selection.kind !== Kind.FIELD ||
        selection.name.value !== 'composition_v3'
      ) {
        continue;
      }

      const reqArg = selection.arguments?.find((a) => a.name.value === 'req');
      if (!reqArg) continue;

      const next = overrideReqArgument(reqArg.value, variables, topN);
      if (next) {
        variables = next;
        changed = true;
      }
    }
  }

  if (!changed) return operation;

  return makeOperation(
    operation.kind,
    { key: operation.key, query: operation.query, variables },
    operation.context
  );
};

export const topNOverrideExchange = (getTopN: () => number | null) =>
  mapExchange({
    onOperation(operation) {
      if (operation.kind !== 'query') return operation;

      const topN = getTopN();
      if (topN === null) return operation;

      return applyTopNOverride(operation, topN);
    },
  });
